import {AnalyticsStateMachine} from '../../analyticsStateMachines/AnalyticsStateMachine';
import {EventDispatcher} from '../../core/EventDispatcher';
import QualityChangeService from '../../core/QualityChangeService';
import {SourceInfoFallbackService} from '../../core/SourceInfoFallbackService';
import {SsaiService} from '../../core/ssai/SsaiService';
import {Event, EventMap} from '../../enums/Event';
import {AnalyticsConfig} from '../../types/AnalyticsConfig';
import {CustomDataValues} from '../../types/CustomDataValues';
import {DownloadSpeedInfo} from '../../types/DownloadSpeedInfo';
import {DrmPerformanceInfo} from '../../types/DrmPerformanceInfo';
import type {CustomDataValuesEvent} from '../../types/EventData';
import {LatencyInfo} from '../../types/LatencyInfo';
import {NoExtraProperties} from '../../types/NoExtraProperties';
import {SegmentInfo} from '../../types/SegmentInfo';
import type {SourceMetadata} from '../../types/SourceMetadata';
import {defaultStateMachineCallbacks, StateMachineCallbacks} from '../../types/StateMachineCallbacks';
import {getGlobalScreenDimensions} from '../../utils/globalScopeUtils';
import {logger} from '../../utils/Logger';
import {WindowEventTracker} from '../../utils/WindowEventTracker';

import {EventCallbackDispatcher} from './EventCallbackDispatcher';

export abstract class InternalAdapter implements EventCallbackDispatcher {
  readonly onLicenseKeyReceived: EventDispatcher<{licenseKey: string}> = new EventDispatcher();
  readonly onLicenseCallFailed: EventDispatcher<any> = new EventDispatcher();
  readonly qualityChangeService: QualityChangeService = new QualityChangeService();
  readonly ssaiService = new SsaiService();
  readonly stateMachineCallbacks: StateMachineCallbacks = {...defaultStateMachineCallbacks};

  protected stateMachine!: AnalyticsStateMachine;
  protected sourceInfoFallbackService: SourceInfoFallbackService = new SourceInfoFallbackService();
  protected drmPerformanceInfo: DrmPerformanceInfo | undefined = undefined;
  protected readonly windowEventTracker: WindowEventTracker = new WindowEventTracker();

  protected abstract get currentTime(): number;

  release() {
    this.stateMachine.resetIntervals();
    this.stateMachineCallbacks.release();
    this.resetSourceRelatedState();
    this.windowEventTracker.release();
  }

  eventCallback<StateMachineEvent extends keyof EventMap, StateMachineEventData extends EventMap[StateMachineEvent]>(
    event: StateMachineEvent,
    eventData: NoExtraProperties<EventMap[StateMachineEvent], StateMachineEventData>,
    eventTimestamp: number = Date.now(),
  ) {
    eventData = eventData || {};
    if (!this.stateMachine) {
      logger.log("Bitmovin Analytics: StateMachine isn't ready yet");
    } else {
      this.stateMachine.callEvent(event, eventData, eventTimestamp);
    }
  }

  getCommonPlaybackInfo() {
    const dimensions = getGlobalScreenDimensions();

    return {
      screenHeight: dimensions?.height,
      screenWidth: dimensions?.width,
    };
  }

  getDrmPerformanceInfo(): DrmPerformanceInfo | undefined {
    return undefined;
  }

  getSegmentInfo(): SegmentInfo[] {
    return [];
  }

  getDownloadSpeedInfo(): DownloadSpeedInfo {
    return {
      segmentsDownloadCount: 0,
      segmentsDownloadSize: 0,
      segmentsDownloadTime: 0,
      avgDownloadSpeed: 0,
      minDownloadSpeed: 0,
      maxDownloadSpeed: 0,
      avgTimeToFirstByte: 0,
    };
  }

  resetDownloadSpeedInfoMeasures(): void {
    // noop by default
  }

  getLatencyInfo(): LatencyInfo | undefined {
    return undefined;
  }

  resetLatencyMeasures(): void {
    // noop by default
  }

  setCustomData(values: CustomDataValues) {
    const previousState = this.stateMachine.currentState;
    const eventData: CustomDataValuesEvent = {customDataValues: values, currentTime: this.currentTime};

    if (
      this.stateMachine.currentState &&
      (this.stateMachine.currentState === 'PAUSE' || this.stateMachine.currentState === 'PLAYING')
    ) {
      // If we are in State.PAUSE or State.PLAYING, we want to make "a cut" of the current state
      // to trigger sample sending with current custom data values.
      this.eventCallback(Event.CUSTOM_DATA_CHANGE, {currentTime: this.currentTime});
      // Then we have to call this "previous state" event to return back
      // to the previous state and to set new custom data values.
      // !! here we relay on the fact that we have `PAUSE` and `PLAYING` events with the same name as the states above !!
      this.eventCallback(Event[previousState], eventData);
    }
    // ... else, for any other states, we don't care, and we will just set new custom data values asap, so they will
    // be sent with the upcoming sample
    else {
      this.stateMachineCallbacks.customdatachange(undefined, undefined, eventData);
    }
  }

  sourceChange(analyticsConfig: AnalyticsConfig): void {
    // A still running SSAI ad has to be closed out as abandoned BEFORE we dispatch MANUAL_SOURCE_CHANGE, because that
    // event starts a new impression.
    //
    // This is the only place where we can do that for:
    //  - the public `sourceChange` API: the customer announces the source change themselves, so there is no player
    //    event we could hook into instead.
    //  - players like the Bitmovin Player, where we wrap the player's `load` function and call `sourceChange` from
    //    that wrapper. The wrapper runs before the player reports its own source unload event, so reacting to that
    //    event would already be too late.
    this.ssaiService.onAbandonment({isClosing: false});
    this.eventCallback(Event.MANUAL_SOURCE_CHANGE, {currentTime: this.currentTime, config: analyticsConfig});
  }

  resetSourceRelatedState(): void {
    this.sourceInfoFallbackService.reset();
    this.drmPerformanceInfo = undefined;
  }

  programChange(sourceMetadata: SourceMetadata): void {
    const isSsaiActive = this.ssaiService.isInActiveState();
    this.stateMachine.programChange(this.currentTime, isSsaiActive, sourceMetadata);
  }
}
